/**
 * Setting one secret value, at whichever layer the form names, as a panel
 * form. The platform fallback, a partner's shared value, and a tenant's own
 * value are the same act against the same field definition; only the
 * endpoint differs. The form's id says which:
 *
 *   global:<key>  ·  org:<orgId>:<key>  ·  partner:<partnerId>:<key>
 *
 * Values are write-only over the wire: the API returns `is_set` and never the
 * plaintext, so an already-set field offers to overwrite rather than edit.
 * Writes demand a recent sign-in; the step-up prompt asks when the API
 * refuses, and the form survives the round trip because it is in the URL.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ApiError } from '@optum/api-client/client';
import { listGlobalSecrets, setGlobalSecret } from '@optum/api-client/endpoints/global-secrets';
import { listOrgSecrets, setOrgSecret } from '@optum/api-client/endpoints/organization-secrets';
import { listPartnerSecrets, setPartnerSecret } from '@optum/api-client/endpoints/partner-secrets';
import type { SecretFieldType } from '@optum/api-client/types';

import { PanelFormActions, PanelFormFields, usePanelFormDone } from '../shell/PanelForm.js';
import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Field } from '../../ui/Field.js';
import { Input, Typography } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';

export type SecretLayer =
  | { layer: 'global'; key: string }
  | { layer: 'org'; orgId: string; key: string }
  | { layer: 'partner'; partnerId: string; key: string };

export function formatSecretTarget(target: SecretLayer): string {
  if (target.layer === 'global') return `global:${target.key}`;
  if (target.layer === 'org') return `org:${target.orgId}:${target.key}`;
  return `partner:${target.partnerId}:${target.key}`;
}

export function parseSecretTarget(id: string | undefined): SecretLayer | null {
  if (!id) return null;
  const parts = id.split(':');
  if (parts[0] === 'global' && parts.length === 2 && parts[1])
    return { layer: 'global', key: parts[1] };
  if (parts[0] === 'org' && parts.length === 3 && parts[1] && parts[2]) {
    return { layer: 'org', orgId: parts[1], key: parts[2] };
  }
  if (parts[0] === 'partner' && parts.length === 3 && parts[1] && parts[2]) {
    return { layer: 'partner', partnerId: parts[1], key: parts[2] };
  }
  return null;
}

const MEANING: Record<SecretLayer['layer'], ReactNode> = {
  global:
    'The platform fallback for this key. An organization that sets its own value for the same key wins at task time.',
  org: "This tenant's own value for the key. It wins over the platform fallback at task time.",
  partner:
    "Shared by every organization this partner provisioned. An organization's own value still wins at task time.",
};

function coerce(raw: string, type: SecretFieldType): unknown {
  switch (type) {
    case 'integer': {
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? raw : n;
    }
    case 'number': {
      const n = Number(raw);
      return Number.isNaN(n) ? raw : n;
    }
    case 'boolean':
      return raw === 'true' || raw === '1' || raw.toLowerCase() === 'yes';
    default:
      return raw;
  }
}

export function SetSecretValueForm({ id }: { id: string | undefined }) {
  const target = parseSecretTarget(id);
  const qc = useQueryClient();
  const toast = useToast();
  const done = usePanelFormDone();
  const [draft, setDraft] = useState('');

  const orgId = target?.layer === 'org' ? target.orgId : '';
  const partnerId = target?.layer === 'partner' ? target.partnerId : '';
  const global = useQuery({
    queryKey: qk.globalSecrets(),
    queryFn: listGlobalSecrets,
    enabled: target?.layer === 'global',
  });
  const org = useQuery({
    queryKey: qk.orgSecrets(orgId),
    queryFn: () => listOrgSecrets(orgId),
    enabled: target?.layer === 'org',
  });
  const partner = useQuery({
    queryKey: qk.partnerSecrets(partnerId),
    queryFn: () => listPartnerSecrets(partnerId),
    enabled: target?.layer === 'partner',
  });
  const [setGlobal, globalPrompt] = useStepUp(setGlobalSecret);
  const [setOrg, orgPrompt] = useStepUp(setOrgSecret);
  const [setPartner, partnerPrompt] = useStepUp(setPartnerSecret);

  const save = useMutation({
    mutationFn: (value: unknown) => {
      if (!target) return Promise.reject(new Error('No secret named'));
      if (target.layer === 'global') return setGlobal(target.key, value);
      if (target.layer === 'org') return setOrg(target.orgId, target.key, value);
      return setPartner(target.partnerId, target.key, value);
    },
    onSuccess: () => {
      if (!target) return;
      toast.success(`Saved ${target.key}`);
      if (target.layer === 'global') qc.invalidateQueries({ queryKey: qk.globalSecrets() });
      if (target.layer === 'org') qc.invalidateQueries({ queryKey: qk.orgSecrets(target.orgId) });
      if (target.layer === 'partner') {
        qc.invalidateQueries({ queryKey: qk.partnerSecrets(target.partnerId) });
      }
      done();
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? e.message : String(e)),
  });

  if (!target) return <ErrorBlock message="This form names no secret." />;
  const list = target.layer === 'global' ? global : target.layer === 'org' ? org : partner;
  if (list.isPending) return <LoadingBlock />;
  if (list.isError) {
    return (
      <ErrorBlock message={list.error instanceof Error ? list.error.message : 'Failed to load'} />
    );
  }
  const entry = (list.data ?? []).find((e) => e.field_key === target.key);
  if (!entry) return <ErrorBlock message={`No secret field ${target.key}.`} />;

  return (
    <>
      {globalPrompt}
      {orgPrompt}
      {partnerPrompt}
      <PanelFormFields onSubmit={() => draft && save.mutate(coerce(draft, entry.type))}>
        <div>
          <Typography variant="eyebrow">{entry.is_set ? 'Replace' : 'Set'}</Typography>
          <div
            className="mono"
            style={{
              marginTop: 5,
              fontSize: 'var(--text-title-sm)',
              fontWeight: 'var(--weight-bold)',
            }}
          >
            {entry.field_key}
          </div>
        </div>
        <Typography variant="body-muted">{MEANING[target.layer]}</Typography>
        <Field id="secret-value" label={entry.label} required hint={entry.type}>
          <Input
            id="secret-value"
            type={entry.is_secret && entry.type === 'string' ? 'password' : 'text'}
            placeholder={entry.is_set ? '(value hidden — type to overwrite)' : 'enter value'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
        </Field>
        <PanelFormActions
          submitLabel="Save"
          pending={save.isPending}
          disabled={!draft}
          dirty={draft !== ''}
        />
      </PanelFormFields>
    </>
  );
}
